import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Rocket, Clock, Tag } from "lucide-react";
import { toast } from "sonner";

/**
 * CycleTemplatesPicker — one-tap start from a curated protocol library.
 *
 * Props:
 *   trigger:  React node (usually a Button) that opens the dialog
 *   onStarted(cycleId?): callback fired after a template is applied so the
 *     parent can reload its cycles list.
 */
export default function CycleTemplatesPicker({ trigger, onStarted }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [starting, setStarting] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/cycles/templates")
      .then(({ data }) => setTemplates(data.templates || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open]);

  const start = async (tpl) => {
    setStarting(tpl.key);
    try {
      await api.post(`/cycles/from-template/${tpl.key}`, {});
      toast.success(`Started: ${tpl.name}`);
      setOpen(false);
      onStarted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStarting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            className="border-teal-800 bg-teal-950/30 text-teal-300 hover:bg-teal-900/40 rounded-none h-11 px-6 uppercase tracking-widest text-xs font-semibold"
            data-testid="cycle-templates-open"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Start from template
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl bg-white border border-slate-200 rounded-none text-slate-900" data-testid="cycle-templates-dialog">
        <DialogHeader>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-teal-400 flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3" /> // Curated Protocols
          </div>
          <DialogTitle className="font-display font-black uppercase tracking-tight text-2xl">
            Start from Template
          </DialogTitle>
          <p className="text-slate-500 text-sm mt-1">
            Pre-configured protocols with sensible defaults. Everything is editable after you save — this is a starting point, not medical advice.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-slate-500 text-sm">Loading templates…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
            {templates.map((tpl) => {
              const busy = starting === tpl.key;
              return (
                <div
                  key={tpl.key}
                  className="border border-slate-200 bg-slate-50/60 p-4 flex flex-col hover:border-teal-700/80 transition-colors"
                  data-testid={`cycle-template-${tpl.key}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="font-mono-num text-[9px] uppercase tracking-widest text-teal-400 flex items-center gap-1">
                      <Tag className="w-2.5 h-2.5" /> {tpl.category}
                    </span>
                    <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> {tpl.duration_days ? `${tpl.duration_days}d` : `${tpl.duration_weeks}w`}
                    </span>
                  </div>
                  <div className="font-display font-bold text-white text-base leading-tight">
                    {tpl.name}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mt-2 font-mono-num text-[11px]">
                    <span className="text-[#0D9488] tabular-nums">{tpl.dose_mg} mg</span>
                    <span className="text-slate-600">· {tpl.frequency}</span>
                    {tpl.weekly_target_mg && (
                      <span className="text-slate-500">· {tpl.weekly_target_mg} mg/wk</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed mt-2 flex-1">
                    {tpl.notes}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(tpl.tags || []).map((t) => (
                      <span key={t} className="border border-slate-200 text-slate-500 px-1.5 py-0.5 font-mono-num text-[9px] uppercase tracking-widest">
                        {t}
                      </span>
                    ))}
                  </div>
                  <Button
                    type="button"
                    onClick={() => start(tpl)}
                    disabled={busy}
                    className="mt-3 w-full h-9 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest text-[11px] font-semibold"
                    data-testid={`cycle-template-start-${tpl.key}`}
                  >
                    <Rocket className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Starting…" : "Start Now"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
